import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import HapperLogo from '../assets/images/logo Happer nonpix.png';
import HapperLogoDesktop from '../assets/images/logo.svg';
import Bag from '../assets/images/bag.svg';
import Profile from '../assets/images/profile.svg';
import { useNavigate, useLocation } from "react-router-dom";
import routes from "../layout/Routes";
import Skeleton from "react-loading-skeleton";
import { useCart } from '../context/CartContext';
import {
  getNoticationsApi,
  getNotificationDetailApi,
  logoutApi,
  getAuthMeApi,
  getStoredUser,
  clearAuthSession,
  isApiSuccess,
  getApiMessage,
  getSelfieSuggestionsApi,
} from "./actioncreator";
import moment from 'moment';
import { formatTimeAgoFr } from '../utils/timeAgo';
import t from '../utils/translate';
import LanguageSwitcher from "./LanguageSwitcher";

const MENU_ITEMS = [
  { labelKey: 'feed', path: routes.CREATOR, icon: 'fa-home' },
  { labelKey: 'discover', path: routes.DISCOVER, icon: 'fa-compass' },
  { labelKey: 'favorites', path: routes.LIKED_IMAGES, icon: 'fa-heart' },
  { labelKey: 'cart', path: routes.MY_CART, icon: 'fa-shopping-bag', showCartBadge: true },
  { labelKey: 'account', path: routes.USER_PROFILE, icon: 'fa-user' },
];

const Header = ({
  searchFilter = { userId: '', brandId: '' },
  setSearchFilter = () => {},
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerView, setDrawerView] = useState('menu');
  const [notificationData, setNotificationData] = useState([]);
  const [selectedNotificationDetail, setSelectedNotificationDetail] = useState(null);
  const [profileUser, setProfileUser] = useState(() => getStoredUser());
  const [searchInput, setSearchInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [suggestBox, setSuggestBox] = useState(null);
  const searchWrapRef = useRef(null);
  const suggestionsRef = useRef(null);
  const allowSuggestionsRef = useRef(true);
  const suggestionsRequestRef = useRef(0);

  const closeSuggestions = () => {
    suggestionsRequestRef.current += 1;
    allowSuggestionsRef.current = false;
    setShowSuggestions(false);
    setSuggestions([]);
    setLoadingSuggestions(false);
  };
  const { cartData } = useCart();
  const cartItemCount = cartData?.items?.length || 0;
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem("btoken");

  const goToCreatorFeed = () => {
    if (location.pathname !== routes.CREATOR) {
      navigate(routes.CREATOR);
    }
  };

  const handleSearchFocus = () => {
    if (location.pathname === routes.DISCOVER) {
      goToCreatorFeed();
    }
    if (allowSuggestionsRef.current && suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  const handleSearchChange = (value) => {
    allowSuggestionsRef.current = true;
    setSearchInput(value);

    if (!value.trim()) {
      setSearchFilter({ userId: '', brandId: '' });
      closeSuggestions();
      allowSuggestionsRef.current = true;
    }
  };

  const handleSuggestionSelect = (item) => {
    const isBrand = item.type === 'brand';
    const label = isBrand ? item.name : (item.username || item.name);

    closeSuggestions();
    setSearchInput(label);
    searchWrapRef.current?.querySelector('input')?.blur();

    if (isBrand) {
      setSearchFilter({ userId: '', brandId: item._id || '' });
      goToCreatorFeed();
      return;
    }

    setSearchFilter({ userId: '', brandId: '' });
    if (item.picture) {
      localStorage.setItem('pimage', item.picture);
    }
    navigate(`/profile/${item._id}`);
  };

  useEffect(() => {
    if (!allowSuggestionsRef.current) {
      return undefined;
    }

    const query = (searchInput || '').trim();

    if (query.length < 1) {
      setSuggestions([]);
      setLoadingSuggestions(false);
      return undefined;
    }

    setLoadingSuggestions(true);
    const requestId = suggestionsRequestRef.current + 1;
    suggestionsRequestRef.current = requestId;

    const timer = setTimeout(() => {
      getSelfieSuggestionsApi(
        token,
        { search: query, page: '', perPage: '' },
        (items) => {
          if (requestId !== suggestionsRequestRef.current) return;
          if (!allowSuggestionsRef.current) return;
          setSuggestions(Array.isArray(items) ? items : []);
          setShowSuggestions(true);
          setLoadingSuggestions(false);
        },
        () => {
          if (requestId !== suggestionsRequestRef.current) return;
          setSuggestions([]);
          setLoadingSuggestions(false);
        }
      );
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput, token]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inSearch = searchWrapRef.current?.contains(event.target);
      const inSuggest = suggestionsRef.current?.contains(event.target);
      if (!inSearch && !inSuggest) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!showSuggestions) {
      setSuggestBox(null);
      return undefined;
    }

    const updatePosition = () => {
      const el = searchWrapRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const width = Math.min(400, Math.max(320, window.innerWidth - 16));
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
      setSuggestBox({
        position: 'fixed',
        top: rect.bottom + 8,
        left,
        width,
        zIndex: 4000,
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [showSuggestions, suggestions, loadingSuggestions, searchInput]);

  const closeModals = () => {
    const modals = document.querySelectorAll('.modal.show');
    modals.forEach(modal => {
      const modalInstance = window.bootstrap.Modal.getInstance(modal);
      if (modalInstance) {
        modalInstance.hide();
      }
    });
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setDrawerView('menu');
    document.body.classList.remove('happer-drawer-open');
  };

  const openDrawer = (view = 'menu') => {
    setDrawerView(view);
    setIsDrawerOpen(true);
    document.body.classList.add('happer-drawer-open');
    fetchNotifications();
  };

  const handleNavigate = (path) => {
    closeModals();
    closeDrawer();
    navigate(path);
  };

  const logout = () => {
    closeModals();
    closeDrawer();
    logoutApi(token, () => {
      clearAuthSession();
      navigate(routes.LOGIN);
    }, () => {
      clearAuthSession();
      navigate(routes.LOGIN);
    });
  };


  const fetchNotifications = () => {
    if (!token) return;

    getNoticationsApi(token, (notifications) => {
      const list = Array.isArray(notifications) ? notifications : [];
      setNotificationData(list.slice().reverse());
    });
  };

  useEffect(() => {
    fetchNotifications();
    return () => document.body.classList.remove('happer-drawer-open');
  }, []);

  useEffect(() => {
    if (!token) return;

    const refreshProfileUser = () => {
      getAuthMeApi(token, (res) => {
        if (isApiSuccess(res)) {
          setProfileUser(res.user || res.data || getStoredUser());
        } else {
          setProfileUser(getStoredUser());
        }
      }, () => {
        setProfileUser(getStoredUser());
      });
    };

    refreshProfileUser();

    const handleProfileUpdated = () => {
      const stored = getStoredUser();
      if (stored) {
        setProfileUser(stored);
      }
      refreshProfileUser();
    };

    window.addEventListener('profile-updated', handleProfileUpdated);
    window.addEventListener('auth-session-changed', handleProfileUpdated);

    return () => {
      window.removeEventListener('profile-updated', handleProfileUpdated);
      window.removeEventListener('auth-session-changed', handleProfileUpdated);
    };
  }, [token]);

  const handleViewNotificationDetail = (id) => {
    getNotificationDetailApi(id, token, res => {
      setSelectedNotificationDetail(res);
      closeDrawer();
      const modal = new window.bootstrap.Modal(document.getElementById('notificationDetailModal'));
      modal.show();
    });
  };

  const truncate = (text = '', length = 50) => {
    return text.length > length ? `${text.substring(0, length)}...` : text;
  };

  const displayName = profileUser
    ? `${profileUser.first_name || ''} ${profileUser.last_name || ''}`.trim() || profileUser.username || t("my_account")
    : t("guest");

  const renderDrawerMenu = () => (
    <ul className="happer-drawer-menu">
      {MENU_ITEMS.map((item) => (
        <li key={item.path}>
          <button type="button" onClick={() => handleNavigate(item.path)}>
            <span className="happer-drawer-menu-icon">
              <i className={`fa ${item.icon}`} />
            </span>
            <span>{t(item.labelKey)}</span>
            {item.showCartBadge && cartItemCount > 0 && (
              <span className="happer-drawer-badge">{cartItemCount}</span>
            )}
          </button>
        </li>
      ))}
      <li className="happer-drawer-lang">
        <div className="happer-drawer-lang-label">
          <span className="happer-drawer-menu-icon">
            <i className="fa fa-globe" />
          </span>
          <span>{t("language")}</span>
        </div>
        <LanguageSwitcher compact />
      </li>
      {/* <li>
        <button type="button" onClick={() => setDrawerView('notifications')}>
          <span className="happer-drawer-menu-icon">
            <i className="fa fa-bell" />
          </span>
          <span>Notifications</span>
          {notificationData.length > 0 && (
            <span className="happer-drawer-badge">{notificationData.length}</span>
          )}
        </button>
      </li> */}
      <li className="happer-drawer-logout">
        <button type="button" onClick={logout}>
          <span className="happer-drawer-menu-icon">
            <i className="fa fa-sign-out" />
          </span>
          <span>{t("logout")}</span>
        </button>
      </li>
    </ul>
  );

  const renderDrawerNotifications = () => (
    <div className="happer-drawer-notifications">
      <div className="happer-notifications-panel happer-notifications-panel--drawer">
        <div className="happer-notifications-header">
          <h1>{t("notifications")}</h1>
          {notificationData.length > 0 && (
            <span className="happer-notifications-count">{notificationData.length}</span>
          )}
        </div>

        {notificationData.length > 0 ? (
          notificationData.map((notification) => {
            const title = notification?.message?.notification?.title || t("title_message");
            const body = notification?.message?.notification?.body || "";
            const createdAt = formatTimeAgoFr(notification.created_at);

            return (
              <div
                className="happer-notifications-item"
                key={notification._id}
                onClick={() => handleViewNotificationDetail(notification._id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleViewNotificationDetail(notification._id)}
              >
                <div className="happer-notifications-item-top">
                  <span className="happer-notifications-item-title">{truncate(title, 40)}</span>
                  <span className="happer-notifications-item-time">{createdAt}</span>
                </div>
                <p className="happer-notifications-item-body">{truncate(body, 90)}</p>
              </div>
            );
          })
        ) : (
          <p className="happer-notifications-empty">{t("no_notifications_yet")}</p>
        )}
      </div>
    </div>
  );

  return (
    <>
      <header className="happer-header">
        <div className="happer-header-inner">
          <div className="happer-header-left">
            <button
              type="button"
              className="happer-menu-btn"
              onClick={() => openDrawer('menu')}
              aria-label={t("open_menu")}
            >
              <span />
              <span />
              <span />
            </button>
            <button
              type="button"
              className="happer-logo-btn"
              onClick={() => handleNavigate(routes.CREATOR)}
            >
              <img src={HapperLogoDesktop} alt="Happer" className="happer-logo-desktop" />
              <img src={HapperLogo} alt="Happer" className="happer-logo-mobile" />
            </button>
          </div>

          <div className="happer-search-wrap" ref={searchWrapRef}>
            <div className="happer-search-pill">
              <i className="fa fa-search" aria-hidden="true" />
              <input
                type="search"
                className="happer-search-input"
                placeholder={t("search_creators_brands")}
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                onFocus={handleSearchFocus}
                aria-label={t("search_creators_brands")}
                aria-expanded={showSuggestions}
                aria-controls="happer-search-suggestions"
                autoComplete="off"
              />
            </div>
          </div>

          <div className="happer-header-actions">
            <button
              type="button"
              className="happer-icon-btn happer-cart-btn"
              onClick={() => handleNavigate(routes.MY_CART)}
              aria-label={t("cart")}
            >
              <img src={Bag} alt="" />
              {cartItemCount > 0 && (
                <span className="happer-badge">{cartItemCount}</span>
              )}
            </button>
            <button
              type="button"
              className="happer-avatar-btn"
              onClick={() => openDrawer('menu')}
              aria-label={t("account_menu")}
            >
              {profileUser ? (
                <img
                  src={profileUser?.picture || profileUser?.profile_image || Profile}
                  alt={displayName}
                  className="happer-avatar-img"
                />
              ) : (
                <Skeleton circle height={42} width={42} />
              )}
            </button>
          </div>
        </div>
      </header>

      <div className="happer-header-spacer" aria-hidden="true" />

      <div
        className={`happer-drawer-overlay ${isDrawerOpen ? 'open' : ''}`}
        onClick={closeDrawer}
        aria-hidden="true"
      />

      <aside className={`happer-drawer ${isDrawerOpen ? 'open' : ''}`} aria-hidden={!isDrawerOpen}>
        <div className="happer-drawer-header">
          {drawerView === 'notifications' ? (
            <>
              <button
                type="button"
                className="happer-drawer-back"
                onClick={() => setDrawerView('menu')}
                aria-label={t("back_to_menu")}
              >
                <i className="fa fa-arrow-left" />
              </button>
              <div className="happer-drawer-header-spacer" />
            </>
          ) : (
            <>
              <div className="happer-drawer-profile">
                {profileUser ? (
                  <img
                    src={profileUser?.picture || profileUser?.profile_image || Profile}
                    alt={displayName}
                    className="happer-drawer-avatar happer-avatar-img"
                  />
                ) : (
                  <Skeleton circle height={56} width={56} />
                )}
                <div>
                  <p className="happer-drawer-label">{t("welcome_back")}</p>
                  <h2>{displayName}</h2>
                </div>
              </div>
              <button
                type="button"
                className="happer-drawer-close"
                onClick={closeDrawer}
                aria-label={t("close_menu")}
              >
                <i className="fa fa-times" />
              </button>
            </>
          )}
        </div>

        <div className="happer-drawer-body">
          {drawerView === 'menu' ? renderDrawerMenu() : renderDrawerNotifications()}
        </div>
      </aside>

      <div className="modal fade" id="notificationDetailModal" tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header border-0">
              <h5 className="modal-title">{t("notification_detail")}</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" />
            </div>
            <div className="modal-body pt-0">
              {selectedNotificationDetail ? (
                <>
                  <h6 className="fw-bold">{selectedNotificationDetail.message?.notification?.title || t("no_title")}</h6>
                  <p className="text-muted small">
                    {moment(selectedNotificationDetail.created_at).format('MMMM Do YYYY, h:mm:ss a')}
                  </p>
                  <p>{selectedNotificationDetail.message?.notification?.body || t("no_content")}</p>

                  {selectedNotificationDetail.message?.android?.notification?.imageUrl ||
                    selectedNotificationDetail.message?.apns?.fcm_options?.image ||
                    selectedNotificationDetail.message?.webpush?.headers?.image ? (
                    <div className="mt-3 text-center">
                      <img
                        src={
                          selectedNotificationDetail.message?.android?.notification?.imageUrl ||
                          selectedNotificationDetail.message?.apns?.fcm_options?.image ||
                          selectedNotificationDetail.message?.webpush?.headers?.image
                        }
                        alt={t("notification")}
                        className="happer-notification-detail-img"
                      />
                    </div>
                  ) : (
                    <div className="text-muted">{t("no_image_provided")}</div>
                  )}
                </>
              ) : (
                <p>{t("loading")}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {showSuggestions && (loadingSuggestions || suggestions.length > 0) && suggestBox &&
        createPortal(
          <div
            ref={suggestionsRef}
            className="happer-search-suggestions"
            id="happer-search-suggestions"
            role="listbox"
            style={suggestBox}
          >
            {loadingSuggestions && (
              <p className="happer-search-suggestions-empty">{t("search")}</p>
            )}
            {!loadingSuggestions && suggestions.map((item) => {
              const isBrand = item.type === 'brand';
              const title = isBrand ? item.name : (item.full_name || item.name);
              const username = (item.username || '').replace(/^@/, '');

              return (
                <button
                  key={`${item.type}-${item._id || item.name}`}
                  type="button"
                  className="happer-search-suggestion"
                  role="option"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSuggestionSelect(item)}
                >
                  {item.picture ? (
                    <img src={item.picture} alt="" className="happer-search-suggestion-avatar" />
                  ) : (
                    <span className={`happer-search-suggestion-icon happer-search-suggestion-icon--${item.type}`}>
                      {(isBrand ? item.name : title).slice(0, 1)}
                    </span>
                  )}
                  <span className="happer-search-suggestion-copy">
                    <strong>{title}</strong>
                    {!isBrand && username ? <small>@{username}</small> : null}
                  </span>
                  <span className="happer-search-suggestion-chip">
                    {isBrand ? t("brand") : t("creator_label")}
                  </span>
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
};

export default Header;
